import { get, chain, size, some, first, last, map, pickBy, find, includes, isEmpty, filter } from "lodash"
import { createSelector } from "reselect"
import { getFactorCategory, getFactorLabel, getIsAggregatedFactor, makeNonZeroKeysSelectorFromData, computeAvailableSelectViewFactors } from "helpers/factors"
import { aggregateExposuresFactors } from "helpers/factorClassification"
import { computeCategoryItemsSubset, computeNonZerosKeys, getAllCategoryItems as getAllCategoryItemsFn, getOrderedCategoryItems as getOrderedCategoryItemsFn } from "helpers/analyze"
import {
  getCategories,
  getDisabled,
  getFactorCategoryId,
  getOnly,
  getAggregationFunction,
} from "."
import { computeFactorSensitivityData } from "helpers/factorSensitivity"
import { getModelFactorsClassification } from "selectors/modelMetadata"

const EMPTY_OBJECT = {}
const EMPTY_ARRAY = []

export const getRawFactorSensitivityData = state => get(state, "global.data.compositeFactorSensitivity.data.model.simulation.factorSensitivity", EMPTY_OBJECT)
const getMustAggregateFactors = (_, props) => props.mustAggregateFactors
const getAggregatedFactorsMetadata = (_, props) => props.aggregatedFactorsMetadata
const getFactorsMetadata = (_, props) => props.factorsMetadata
const getFavoriteFactors = (_, props) => props.favoriteFactors
const getSelectedViews = (_, props) => props.selectedViews
const getFactorsClassification = (_, props) => props.factorsClassification
const getSelectedViewsFactors = (_, props) => props.selectedViewsFactors
const getStartDate = (_, props) => props.startDate
const getEndDate = (_, props) => props.endDate

function formatFactorSensitivity(factorSensitivity, selectedViewFactors, selectedViews, mustAggregateFactors, factorsClassification, styleAggregation) {
  const count = size(factorSensitivity)
  return chain(factorSensitivity)
    .map(({ date, factors }, index) => {
      // Always return last date
      if (factors.length || index === count - 1) {
        let sensFactors = factors
        if (mustAggregateFactors) {
          sensFactors = aggregateExposuresFactors(factorsClassification, factors, styleAggregation, "sensitivity")
        }

        if (size(selectedViews)) {
          sensFactors = chain(factors)
            .filter(factor => some(selectedViewFactors, item => item.id === factor.id))
            .map(factor => ({ ...factor, customView: true }))
            .concat(sensFactors)
            .value()
        }

        return ({
          date,
          factors: chain(sensFactors)
            .keyBy("id")
            .mapValues("sensitivity")
            .value(),
        })
      }
      return null
    })
    .compact()
    .value()
}

export const getFactorSensitivityData = createSelector(
  [getRawFactorSensitivityData, getSelectedViewsFactors, getSelectedViews, getMustAggregateFactors, getModelFactorsClassification, getAggregationFunction],
  formatFactorSensitivity,
)

const getNonZerosKeysForViewFactors = makeNonZeroKeysSelectorFromData({
  getter: value => get(value, "factors"),
  isAboveZeroTreshold: (value) => Math.abs(value) !== 0,
  dataSelector: getRawFactorSensitivityData,
  favoriteSelector: getFavoriteFactors,
  factorValueField: "sensitivity",
})

export const getAvailableSelectViewFactors = createSelector(
  [getAggregatedFactorsMetadata, getFactorsClassification, getSelectedViews, getNonZerosKeysForViewFactors],
  computeAvailableSelectViewFactors,
)

export const getFirst = createSelector(
  [getFactorSensitivityData],
  first,
)

export const getCurrent = createSelector(
  [getFactorSensitivityData],
  last,
)

export const getAllItems = createSelector(
  [getFactorsMetadata, getCurrent, getOnly, getDisabled, getFavoriteFactors],
  (factorsMetadata, lastDatum, only, disabled, favoriteFactors) => map(factorsMetadata, (_, id) => ({
    id,
    label: getFactorLabel(factorsMetadata, id),
    category: getFactorCategory(factorsMetadata, id),
    favorite: get(favoriteFactors, id),
    lastValue: get(lastDatum, ["factors", id], 0) * 100,
    field: `factors.${id}`,
    disabled: only ? only !== id : includes(disabled, id),
    isAggregated: getIsAggregatedFactor(factorsMetadata, id),
    selectedView: factorsMetadata[id].selectedView,
  })),
)

export const getFilteredFactorSensitivity = createSelector(
  [getFactorSensitivityData, getStartDate, getEndDate],
  (factorSensitivity, startDate, endDate) =>
    filter(factorSensitivity, data => data.date >= startDate && data.date <= endDate),
)

export const getFirstFactorSensitivityDate = createSelector(
  [getFilteredFactorSensitivity],
  (factorSensitivity) => chain(factorSensitivity).first().get("date", null).value(),
)

export const getData = createSelector(
  [getAllItems, getFilteredFactorSensitivity],
  computeFactorSensitivityData,
)

const getNonZerosKeys = createSelector(
  [getData, getFavoriteFactors],
  computeNonZerosKeys,
)

const getDisplayCategories = createSelector(
  [getAllItems, getNonZerosKeys],
  (items, keys) => chain(items)
    .filter(item => keys[item.id])
    .map(item => [item.category, item.selectedView])
    .flatten()
    .compact()
    .uniq()
    .value(),
)

export const getOptions = createSelector(
  [getCategories, getFirst, getCurrent, getDisplayCategories],
  (categories, first, current, displayCategories) => {
    if (first && current) {
      return categories.filter(category => includes(displayCategories, category.id))
    } else {
      return EMPTY_ARRAY
    }
  },
)

export const getSelectedOption = createSelector(
  [getOptions, getFactorCategoryId],
  (options, categoryId) => find(options, { id: categoryId }),
)

export const getSelectedFactorCategoryId = createSelector(
  [getSelectedOption],
  (selectedOption) => get(selectedOption, "id"),
)

export const getSelectedFactorCategoryName = createSelector(
  [getSelectedOption],
  (selectedOption) => get(selectedOption, "label"),
)

export const getAllCategoryItems = createSelector(
  [getAllItems, getSelectedFactorCategoryId, getAggregationFunction],
  getAllCategoryItemsFn,
)

export const getOrderedCategoryItems = createSelector(
  [getAllItems, getSelectedFactorCategoryId, getAggregationFunction],
  getOrderedCategoryItemsFn,
)

export const getCategoryItemsSubset = createSelector(
  [getOrderedCategoryItems, getNonZerosKeys],
  computeCategoryItemsSubset,
)

export const getFilteredData = createSelector(
  [getData, getNonZerosKeys, getCategoryItemsSubset],
  (data, keys, items) => isEmpty(items) ? EMPTY_ARRAY : map(data, item => pickBy(item, (_, key) => keys[key])),
)
